import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api, type Me, type TurPdfBelgeTuru, type TurPdfKaynak, type TurPdfKural, type TurPdfProfil, type TurPdfSurum } from './api';
import { Alan, AlanSatiri, Notices, describe } from './Workflow';
import { TUR_PDF as T } from './metin/turPdf';
import { tarihSaat } from './metin/ortak';
import { dosyaBase64, ekOptimizasyonBildirimi, islemAnahtari, islemEngeli, kbHatasi, kuralHatasi, mbMetni, sayfaHatasi, sikistirmaBildirimi, sinirBayti } from './turPdf/mantik';
import './tur-pdf.css';
import { TopluPdf } from './turPdf/TopluPdf';

const TURLER: TurPdfBelgeTuru[] = ['TUR', 'OTEL', 'UCUS', 'DIGER'];

interface Sonuc { url: string; fileName: string; matches: number; pages: number; sikistirma?: string | null }
interface Taslak { id: string | null; version: number; ad: string; tur: TurPdfBelgeTuru; tedarikciId: string; kb: string; kurallar: TurPdfKural[] }

function indirUrl(url: string, ad: string) {
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = ad;
  document.body.append(anchor); anchor.click(); anchor.remove();
}

/**
 * Operasyon / Tur PDF Düzenleme. Tarayıcı yalnız CRM API'sini çağırır; PDF servisine gitmez.
 * Kaynak sayfa (ek PDF'in içinden) ile çıktıdaki sıra (sonuç PDF'inde) ayrı ve ayrı etiketli iki alandır.
 */
export function TurPdfWorkspace({ me }: { me: Me }) {
  const yonetebilir = me.permissions.includes('tour-pdf:manage');
  const [hata, setHata] = useState<string | null>(null);
  const [bildirim, setBildirim] = useState<string | null>(null);
  const [calisiyor, setCalisiyor] = useState<string | null>(null);

  const [arama, setArama] = useState('');
  const [sayfa, setSayfa] = useState(1);
  const [toplam, setToplam] = useState(0);
  const [kaynakYukleniyor, setKaynakYukleniyor] = useState(false);
  const [kaynaklar, setKaynaklar] = useState<TurPdfKaynak[]>([]);
  const [belgeId, setBelgeId] = useState<string | null>(null);
  const [surumler, setSurumler] = useState<TurPdfSurum[]>([]);
  const [parola, setParola] = useState('');

  const [profiller, setProfiller] = useState<TurPdfProfil[]>([]);
  const [turSuzgeci, setTurSuzgeci] = useState<'' | TurPdfBelgeTuru>('');
  const [profilId, setProfilId] = useState('');
  const [tedarikciler, setTedarikciler] = useState<{ id: string; name: string }[]>([]);

  const [kaynakSayfa, setKaynakSayfa] = useState('1');
  const [ciktiSirasi, setCiktiSirasi] = useState('2');
  const [maxKb, setMaxKb] = useState('500');
  const [ekKaldirmaOnayi, setEkKaldirmaOnayi] = useState(false);
  const [taslak, setTaslak] = useState<Taslak | null>(null);
  const [sonuc, setSonuc] = useState<Sonuc | null>(null);
  const mesgul = useRef(false);
  const kaynakIstek = useRef(0);
  const surumIstek = useRef(0);
  const anahtar = useRef<{ imza: string; anahtar: string } | null>(null);

  const profil = profiller.find((entry) => entry.id === profilId) ?? null;
  const belge = kaynaklar.find((entry) => entry.id === belgeId) ?? null;
  const gorunenProfiller = useMemo(() => profiller.filter((entry) => !turSuzgeci || entry.documentType === turSuzgeci), [profiller, turSuzgeci]);

  const sonucuTemizle = useCallback(() => {
    setSonuc((onceki) => { if (onceki) URL.revokeObjectURL(onceki.url); return null; });
  }, []);
  useEffect(() => () => { setSonuc((onceki) => { if (onceki) URL.revokeObjectURL(onceki.url); return null; }); }, []);

  const kaynaklariYukle = useCallback(async (q: string, hedefSayfa = 1, secim: 'temizle' | 'koru' = 'temizle', yeniId?: string) => {
    const istek = ++kaynakIstek.current;
    setKaynakYukleniyor(true);
    if (secim === 'temizle') { setBelgeId(null); setParola(''); setKaynaklar([]); }
    try {
      const liste = await api.turPdfKaynaklar(q.trim() || undefined, hedefSayfa);
      if (istek !== kaynakIstek.current) return;
      setKaynaklar(liste.items); setSayfa(liste.page); setToplam(liste.total);
      setBelgeId((onceki) => {
        const aday = yeniId ?? (secim === 'koru' ? onceki : null);
        return liste.items.some((entry) => entry.id === aday) ? aday : null;
      });
    } finally { if (istek === kaynakIstek.current) setKaynakYukleniyor(false); }
  }, []);
  const profilleriYukle = useCallback(async (secilecek?: string) => {
    const liste = await api.turPdfProfiller();
    setProfiller(liste.items);
    if (secilecek !== undefined) setProfilId(secilecek);
  }, []);
  const surumleriYukle = useCallback(async (id: string | null) => {
    const istek = ++surumIstek.current;
    setSurumler([]);
    if (!id) return;
    try {
      const liste = await api.turPdfSurumler(id);
      if (istek === surumIstek.current) setSurumler(liste.items);
    } catch { if (istek === surumIstek.current) setSurumler([]); }
  }, []);

  const calistir = useCallback(async (ad: string, is: () => Promise<string | void>) => {
    if (mesgul.current) return;
    mesgul.current = true;
    setHata(null); setBildirim(null); setCalisiyor(ad);
    try { const mesaj = await is(); if (typeof mesaj === 'string') setBildirim(mesaj); }
    catch (yakalanan) {
      setHata(describe(yakalanan));
      // Eski ekran: profil baska yerde degismis. Guncel haliyle yeniden yukle ki kullanici dogru surumu gorsun.
      if (yakalanan instanceof ApiError && yakalanan.status === 409) await profilleriYukle().catch(() => undefined);
    }
    finally { mesgul.current = false; setCalisiyor(null); }
  }, [profilleriYukle]);

  useEffect(() => {
    void calistir('yukle', async () => {
      await Promise.all([kaynaklariYukle(''), profilleriYukle()]);
      if (yonetebilir) {
        try { setTedarikciler((await api.supplierOptions()).items.map((entry) => ({ id: entry.id, name: entry.name }))); } catch { setTedarikciler([]); }
      }
    });
  }, [calistir, kaynaklariYukle, profilleriYukle, yonetebilir]);

  // Profil degisince ekranin iki sayfa alani profilden dolar; sonuc ve tekrar-deneme anahtari sifirlanir.
  useEffect(() => {
    if (profil) { setKaynakSayfa(String(profil.attachmentPage)); setCiktiSirasi(String(profil.insertAt)); setMaxKb(String(profil.maxKbPerPage)); }
    setEkKaldirmaOnayi(false);
  }, [profil?.id, profil?.version, profil?.attachmentPage, profil?.insertAt]);
  useEffect(() => { sonucuTemizle(); anahtar.current = null; }, [belgeId, profilId, profil?.version, parola, sonucuTemizle]);
  useEffect(() => { void surumleriYukle(belgeId); }, [belgeId, surumleriYukle]);

  const engel = belge?.sourceAvailable === false ? T.kaynakSuresiDoldu : islemEngeli({ belgeSecili: belge !== null, profilSecili: profil !== null, kuralSayisi: profil?.rules.length ?? 0 }, T);
  const govde = () => ({ documentId: belgeId as string, profileId: (profil as TurPdfProfil).id, profileVersion: (profil as TurPdfProfil).version, ...(parola ? { password: parola } : {}) });

  const onizle = () => calistir('onizle', async () => {
    sonucuTemizle();
    const sonucu = await api.turPdfIsle(govde());
    setSonuc({ url: URL.createObjectURL(sonucu.blob), fileName: sonucu.fileName, matches: sonucu.matches, pages: sonucu.pages, sikistirma: sikistirmaBildirimi({ step: sonucu.compressionStep, originalBytes: sonucu.originalBytes, outputBytes: sonucu.outputBytes }) });
    return [T.eslesme(sonucu.matches, sonucu.pages), sikistirmaBildirimi({ step: sonucu.compressionStep, originalBytes: sonucu.originalBytes, outputBytes: sonucu.outputBytes })].filter(Boolean).join(' ');
  });
  const indir = () => calistir('indir', async () => {
    let hedef = sonuc;
    if (!hedef) {
      const sonucu = await api.turPdfIsle(govde());
      hedef = { url: URL.createObjectURL(sonucu.blob), fileName: sonucu.fileName, matches: sonucu.matches, pages: sonucu.pages, sikistirma: sikistirmaBildirimi({ step: sonucu.compressionStep, originalBytes: sonucu.originalBytes, outputBytes: sonucu.outputBytes }) };
      setSonuc(hedef);
    }
    indirUrl(hedef.url, hedef.fileName);
    return [T.indirildi(hedef.fileName), hedef.sikistirma].filter(Boolean).join(' ');
  });
  const kaydet = () => calistir('kaydet', async () => {
    const imza = `${belgeId}|${profil?.id}|${profil?.version}|${parola}`;
    anahtar.current = islemAnahtari(anahtar.current, imza, () => crypto.randomUUID());
    const sonucu = await api.turPdfKaydet({ ...govde(), processKey: anahtar.current.anahtar });
    // Basarili kayittan sonra anahtar biter: bilincli ikinci kayit yeni surum olur. Hata ya da kopan yanit
    // durumunda anahtar KALIR; tekrar deneme ayni kaydi dondurur, ikinci surum uretmez.
    anahtar.current = null;
    await Promise.all([surumleriYukle(belgeId), kaynaklariYukle(arama, sayfa, 'koru')]);
    if (sonucu.reused) return T.zatenKayitli(sonucu.version.versionNo ?? 0);
    return [T.kaydedildi(sonucu.version.fileName, sonucu.version.versionNo ?? 0), sikistirmaBildirimi(sonucu.compression)].filter(Boolean).join(' ');
  });

  const dosyaYukle = (dosya: File | undefined, hedef: 'kaynak' | 'ek') => {
    if (!dosya) return;
    void calistir(hedef === 'kaynak' ? 'kaynak-yukle' : 'ek-yukle', async () => {
      const icerik = dosyaBase64(await dosya.arrayBuffer());
      if (hedef === 'kaynak') {
        const yeni = await api.turPdfKaynakYukle({ fileName: dosya.name, contentBase64: icerik });
        setArama('');
        await kaynaklariYukle('', 1, 'temizle', yeni.id);
        return T.yuklendi(dosya.name);
      }
      const guncel = await api.turPdfEkYukle((profil as TurPdfProfil).id, { expectedVersion: (profil as TurPdfProfil).version, fileName: dosya.name, contentBase64: icerik });
      await profilleriYukle(guncel.id);
      return [T.ekYuklendi, ekOptimizasyonBildirimi(guncel.optimization)].filter(Boolean).join(' ');
    });
  };

  const sayfaAyariniKaydet = () => calistir('sayfa-kaydet', async () => {
    const sorun = sayfaHatasi(kaynakSayfa, 'Kaynak sayfa', profil?.attachment?.pages ?? null) ?? sayfaHatasi(ciktiSirasi, 'Çıktıdaki sıra');
    if (sorun) throw new Error(sorun);
    const guncel = await api.turPdfProfilGuncelle((profil as TurPdfProfil).id, { expectedVersion: (profil as TurPdfProfil).version, attachmentPage: Number(kaynakSayfa), insertAt: Number(ciktiSirasi) });
    await profilleriYukle(guncel.id);
    return T.profilKaydedildi(guncel.version);
  });

  const kbKaydet = () => calistir('kb-kaydet', async () => {
    const sorun = kbHatasi(maxKb);
    if (sorun) throw new Error(sorun);
    const guncel = await api.turPdfProfilGuncelle((profil as TurPdfProfil).id, { expectedVersion: (profil as TurPdfProfil).version, maxKbPerPage: Number(maxKb) });
    await profilleriYukle(guncel.id);
    return T.profilKaydedildi(guncel.version);
  });

  const ekiKaldir = () => calistir('ek-kaldir', async () => {
    const sonucu = await api.turPdfEkKaldir((profil as TurPdfProfil).id, (profil as TurPdfProfil).version);
    setEkKaldirmaOnayi(false);
    await profilleriYukle(sonucu.profile.id);
    return T.ekKaldirildi;
  });

  const taslakKaydet = () => calistir('profil-kaydet', async () => {
    if (!taslak) return;
    const sorun = kuralHatasi(taslak.kurallar);
    if (sorun) throw new Error(sorun);
    if (taslak.ad.trim() === '') throw new Error(`${T.ad} boş olamaz.`);
    const kbSorunu = taslak.kb.trim() === '' ? null : kbHatasi(taslak.kb);
    if (kbSorunu) throw new Error(kbSorunu);
    if (taslak.id === null) {
      const yeni = await api.turPdfProfilYarat({ name: taslak.ad.trim(), documentType: taslak.tur, ...(taslak.tedarikciId ? { supplierId: taslak.tedarikciId } : {}), rules: taslak.kurallar, ...(taslak.kb.trim() !== '' ? { maxKbPerPage: Number(taslak.kb) } : {}) });
      await profilleriYukle(yeni.id);
      setTaslak(null);
      return T.profilOlusturuldu;
    }
    const guncel = await api.turPdfProfilGuncelle(taslak.id, { expectedVersion: taslak.version, name: taslak.ad.trim(), documentType: taslak.tur, supplierId: taslak.tedarikciId, rules: taslak.kurallar, ...(taslak.kb.trim() !== '' ? { maxKbPerPage: Number(taslak.kb) } : {}) });
    await profilleriYukle(guncel.id);
    setTaslak(null);
    return T.profilKaydedildi(guncel.version);
  });

  const kuralYaz = (sira: number, yama: Partial<TurPdfKural>) =>
    setTaslak((onceki) => onceki && ({ ...onceki, kurallar: onceki.kurallar.map((kural, i) => (i === sira ? { ...kural, ...yama } : kural)) }));

  return <section className="tur-pdf" data-testid="tur-pdf-panel" aria-labelledby="tur-pdf-baslik">
    <h3 id="tur-pdf-baslik">{T.title}</h3>
    <p className="tur-pdf-aciklama">{T.intro}</p>
    <Notices scope="tur-pdf" error={hata} notice={bildirim} />

    <div className="tur-pdf-bolum" data-testid="tur-pdf-belge-bolumu">
      <h4>{T.belgeBolumu}</h4>
      <AlanSatiri>
        <Alan etiket={T.belgeAra} ipucu={T.belgeAraIpucu}>
          <input disabled={calisiyor !== null} type="search" data-testid="tur-pdf-source-search" value={arama}
            onChange={(event) => { setArama(event.target.value); void kaynaklariYukle(event.target.value).catch((err) => setHata(describe(err))); }} />
        </Alan>
        <Alan etiket={T.yeniPdf} ipucu={T.yeniPdfIpucu}>
          <input type="file" accept="application/pdf" data-testid="tur-pdf-source-upload" disabled={calisiyor !== null}
            onChange={(event) => { dosyaYukle(event.target.files?.[0], 'kaynak'); event.target.value = ''; }} />
        </Alan>
      </AlanSatiri>
      {kaynakYukleniyor ? <p role="status">{T.yukleniyor}</p> : null}
      {!kaynakYukleniyor && (kaynaklar.length === 0 ? <p className="ops-document-hint" data-testid="tur-pdf-source-empty">{T.belgeYok}</p>
        : <ul className="tur-pdf-liste" role="radiogroup" aria-label={T.belgeSec}>{kaynaklar.map((entry) => <li key={entry.id}>
          <label><input disabled={calisiyor !== null} type="radio" name="tur-pdf-kaynak" data-testid={`tur-pdf-source-${entry.id}`} checked={belgeId === entry.id} onChange={() => setBelgeId(entry.id)} />
            <span><strong>{entry.fileName}</strong><small>{tarihSaat(entry.uploadedAt)} · {T.surumSayisi(entry.versionCount)}</small>{entry.sourceAvailable === false ? <small>{T.kaynakSuresiDoldu}</small> : null}</span></label>
          {entry.sourceAvailable === false ? null : <button disabled={calisiyor !== null} type="button" className="link-button" data-testid={`tur-pdf-source-download-${entry.id}`} onClick={() => void calistir('kaynak-indir', async () => { await api.turPdfBelgeIndir(entry.id, entry.fileName); return T.indirildi(entry.fileName); })}>{T.indir}</button>}
        </li>)}</ul>)}
      {toplam > 0 ? <nav className="pagination ops-pagination" aria-label={T.belgeSayfalama}>
        <p data-testid="tur-pdf-source-total" aria-live="polite">{T.sayfaOzeti(sayfa, Math.max(1, Math.ceil(toplam / 5)), toplam)}</p>
        {toplam > 5 ? <div>
          <button type="button" className="ikincil" data-testid="tur-pdf-source-prev" disabled={calisiyor !== null || kaynakYukleniyor || sayfa <= 1}
            onClick={() => void kaynaklariYukle(arama, sayfa - 1).catch((err) => setHata(describe(err)))}>{T.onceki}</button>
          <button type="button" className="ikincil" data-testid="tur-pdf-source-next" disabled={calisiyor !== null || kaynakYukleniyor || sayfa * 5 >= toplam}
            onClick={() => void kaynaklariYukle(arama, sayfa + 1).catch((err) => setHata(describe(err)))}>{T.sonraki}</button>
        </div> : null}
      </nav> : null}
      <Alan etiket={T.parola} ipucu={T.parolaIpucu}>
        <input disabled={calisiyor !== null} type="password" autoComplete="off" data-testid="tur-pdf-password" value={parola} onChange={(event) => setParola(event.target.value)} />
      </Alan>
    </div>

    <div className="tur-pdf-bolum" data-testid="tur-pdf-profil-bolumu">
      <h4>{T.profilBolumu}</h4>
      <AlanSatiri>
        <Alan etiket={T.turSuzgeci}>
          <select disabled={calisiyor !== null} data-testid="tur-pdf-type-filter" value={turSuzgeci} onChange={(event) => { setTurSuzgeci(event.target.value as '' | TurPdfBelgeTuru); setProfilId(''); }}>
            <option value="">{T.tumTurler}</option>
            {TURLER.map((tur) => <option key={tur} value={tur}>{T.belgeTurleri[tur]}</option>)}
          </select>
        </Alan>
        <Alan etiket={T.profilSec}>
          <select disabled={calisiyor !== null} data-testid="tur-pdf-profile" value={profilId} onChange={(event) => setProfilId(event.target.value)}>
            <option value="">{T.profilSecilmedi}</option>
            {gorunenProfiller.map((entry) => <option key={entry.id} value={entry.id}>{T.belgeTurleri[entry.documentType]} · {entry.name}</option>)}
          </select>
        </Alan>
      </AlanSatiri>
      {gorunenProfiller.length === 0 ? <p className="ops-document-hint" data-testid="tur-pdf-profile-empty">{yonetebilir ? T.profilYokYonetici : T.profilYok}</p> : null}
      {profil ? <p className="ops-document-hint" data-testid="tur-pdf-profile-meta">{T.tedarikci}: {profil.supplier?.name ?? T.tedarikciYok} · {T.profilSurumu(profil.version)}</p> : null}
    </div>

    {profil ? <div className="tur-pdf-bolum" data-testid="tur-pdf-kural-bolumu">
      <h4>{T.kuralBolumu}</h4>
      <p className="tur-pdf-aciklama">{T.kuralAciklama} {T.kuralSayisi(profil.rules.length)}.</p>
      {profil.rules.length === 0 ? <p>{T.kuralYok}</p> : <div className="tur-pdf-tablo-kabi"><table className="tur-pdf-tablo" data-testid="tur-pdf-rules"><thead><tr><th>{T.aranan}</th><th>{T.yeniMetin}</th></tr></thead>
        <tbody>{profil.rules.map((kural, sira) => <tr key={sira}><td data-label={T.aranan}>{kural.ara}</td><td data-label={T.yeniMetin}>{kural.yeni === '' ? <em>{T.silinir}</em> : kural.yeni}</td></tr>)}</tbody></table></div>}
    </div> : null}

    {profil ? <div className="tur-pdf-bolum" data-testid="tur-pdf-ek-bolumu">
      <h4>{T.ekBolumu}</h4>
      {profil.attachment ? <>
        <p data-testid="tur-pdf-attachment"><strong>{T.ekDosya}:</strong> {profil.attachment.fileName} · {T.ekSayfaSayisi(profil.attachment.pages)}{' '}
          <button disabled={calisiyor !== null} type="button" className="link-button" onClick={() => void calistir('ek-ac', async () => { await api.turPdfEkIndir(profil.id, profil.attachment?.fileName ?? 'ek.pdf'); })}>{T.ekOnizle}</button></p>
        <AlanSatiri>
          <Alan etiket={T.kaynakSayfa} ipucu={T.kaynakSayfaIpucu}>
            <input disabled={calisiyor !== null} type="number" inputMode="numeric" min={1} max={profil.attachment.pages ?? undefined} data-testid="tur-pdf-source-page" value={kaynakSayfa} readOnly={!yonetebilir} onChange={(event) => setKaynakSayfa(event.target.value)} />
          </Alan>
          <Alan etiket={T.cikti} ipucu={T.ciktiIpucu}>
            <input disabled={calisiyor !== null} type="number" inputMode="numeric" min={1} data-testid="tur-pdf-insert-at" value={ciktiSirasi} readOnly={!yonetebilir} onChange={(event) => setCiktiSirasi(event.target.value)} />
          </Alan>
        </AlanSatiri>
        <p className="ops-document-hint" data-testid="tur-pdf-position-summary">{T.ozetSira(profil.attachmentPage, profil.insertAt)}</p>
        {yonetebilir ? <div className="tur-pdf-eylemler">
          <button type="button" className="ikincil" data-testid="tur-pdf-position-save" disabled={calisiyor !== null} onClick={() => void sayfaAyariniKaydet()}>{T.kaydet}</button>
          {ekKaldirmaOnayi ? <span className="tur-pdf-onay" role="alert" data-testid="tur-pdf-remove-warning">{T.ekKaldirUyari}{' '}
            <button type="button" className="birincil" data-testid="tur-pdf-remove-confirm" disabled={calisiyor !== null} onClick={() => void ekiKaldir()}>{T.ekKaldirOnay}</button>{' '}
            <button disabled={calisiyor !== null} type="button" className="ikincil" onClick={() => setEkKaldirmaOnayi(false)}>{T.ekKaldirVazgec}</button></span>
            : <button type="button" className="ikincil" data-testid="tur-pdf-remove-attachment" disabled={calisiyor !== null} onClick={() => setEkKaldirmaOnayi(true)}>{T.ekKaldir}</button>}
        </div> : null}
      </> : <p data-testid="tur-pdf-attachment-empty">{T.ekYok}</p>}
      <AlanSatiri>
        <Alan etiket={T.maxKb} ipucu={T.maxKbIpucu}>
          <input disabled={calisiyor !== null} type="number" inputMode="numeric" min={100} max={2000} data-testid="tur-pdf-max-kb" value={maxKb} readOnly={!yonetebilir} onChange={(event) => setMaxKb(event.target.value)} />
        </Alan>
        <p className="ops-document-hint" data-testid="tur-pdf-limit-summary">{T.maxKbOzet(4, profil.maxKbPerPage, mbMetni(sinirBayti(4, profil.maxKbPerPage)))}</p>
      </AlanSatiri>
      {yonetebilir ? <div className="tur-pdf-eylemler"><button type="button" className="ikincil" data-testid="tur-pdf-kb-save" disabled={calisiyor !== null} onClick={() => void kbKaydet()}>{T.kaydet}</button></div> : null}
      {yonetebilir ? <Alan etiket={T.ekYukle} ipucu={T.ekOptimizeUyari}>
        <input type="file" accept="application/pdf" data-testid="tur-pdf-attachment-upload" disabled={calisiyor !== null}
          onChange={(event) => { dosyaYukle(event.target.files?.[0], 'ek'); event.target.value = ''; }} />
      </Alan> : null}
    </div> : null}

    <TopluPdf profil={profil} busy={calisiyor !== null} run={calistir} onProfileConflict={profilleriYukle} onUploaded={async () => { setArama(''); await kaynaklariYukle(''); }} />

    <div className="tur-pdf-bolum" data-testid="tur-pdf-islem-bolumu">
      <h4>{T.islemBolumu}</h4>
      <div className="tur-pdf-eylemler">
        <button type="button" className="ikincil" data-testid="tur-pdf-preview" disabled={engel !== null || calisiyor !== null} title={engel ?? undefined} onClick={() => void onizle()}>{calisiyor === 'onizle' ? T.isleniyor : T.onizle}</button>
        <button type="button" className="ikincil" data-testid="tur-pdf-download" disabled={engel !== null || calisiyor !== null} title={engel ?? undefined} onClick={() => void indir()}>{calisiyor === 'indir' ? T.isleniyor : T.indir}</button>
        <button type="button" className="birincil" data-testid="tur-pdf-save" disabled={engel !== null || calisiyor !== null} title={engel ?? undefined} onClick={() => void kaydet()}>{calisiyor === 'kaydet' ? T.kaydediliyor : T.yeniSurum}</button>
      </div>
      {engel ? <p className="ops-document-hint" role="status" data-testid="tur-pdf-blocked">{engel}</p> : null}
      {sonuc ? <iframe className="tur-pdf-onizleme" data-testid="tur-pdf-frame" title={T.onizlemeBaslik} src={sonuc.url} /> : null}
    </div>

    {belge ? <div className="tur-pdf-bolum" data-testid="tur-pdf-surum-bolumu">
      <h4>{T.surumlerBaslik}</h4>
      {surumler.length === 0 ? <p data-testid="tur-pdf-versions-empty">{T.surumYok}</p> : <ul className="tur-pdf-liste" data-testid="tur-pdf-versions">{surumler.map((entry) => <li key={entry.id}>
        <span><strong>{T.surumNo(entry.versionNo ?? 0)}</strong><small>{entry.fileName} · {tarihSaat(entry.uploadedAt)}</small></span>
        <button disabled={calisiyor !== null} type="button" className="link-button" onClick={() => void calistir('surum-indir', async () => { await api.turPdfBelgeIndir(entry.id, entry.fileName); return T.indirildi(entry.fileName); })}>{T.indir}</button>
      </li>)}</ul>}
    </div> : null}

    <div className="tur-pdf-bolum" data-testid="tur-pdf-yonetim-bolumu">
      <h4>{T.yonetimBolumu}</h4>
      {!yonetebilir ? <p className="ops-document-hint" data-testid="tur-pdf-manage-denied">{T.yonetimYok}</p> : <>
        <p className="tur-pdf-aciklama">{T.yonetimAciklama} {T.maksKural}</p>
        {taslak === null ? <div className="tur-pdf-eylemler">
          <button disabled={calisiyor !== null} type="button" className="ikincil" data-testid="tur-pdf-new-profile" onClick={() => setTaslak({ id: null, version: 0, ad: '', tur: 'TUR', tedarikciId: '', kb: '500', kurallar: [{ ara: '', yeni: '' }] })}>{T.yeniProfil}</button>
          {profil ? <button disabled={calisiyor !== null} type="button" className="ikincil" data-testid="tur-pdf-edit-profile" onClick={() => setTaslak({ id: profil.id, version: profil.version, ad: profil.name, tur: profil.documentType, tedarikciId: profil.supplier?.id ?? '', kb: String(profil.maxKbPerPage), kurallar: profil.rules.map((kural) => ({ ...kural })) })}>{T.duzenle}</button> : null}
        </div> : <div className="tur-pdf-form" data-testid="tur-pdf-profile-form">
          <AlanSatiri>
            <Alan etiket={T.ad} zorunlu><input disabled={calisiyor !== null} type="text" maxLength={120} data-testid="tur-pdf-manage-name" value={taslak.ad} onChange={(event) => setTaslak({ ...taslak, ad: event.target.value })} /></Alan>
            <Alan etiket={T.belgeTuru}><select disabled={calisiyor !== null} data-testid="tur-pdf-manage-type" value={taslak.tur} onChange={(event) => setTaslak({ ...taslak, tur: event.target.value as TurPdfBelgeTuru })}>{TURLER.map((tur) => <option key={tur} value={tur}>{T.belgeTurleri[tur]}</option>)}</select></Alan>
            <Alan etiket={T.maxKb} ipucu={T.maxKbIpucu}><input disabled={calisiyor !== null} type="number" inputMode="numeric" min={100} max={2000} data-testid="tur-pdf-manage-kb" value={taslak.kb} onChange={(event) => setTaslak({ ...taslak, kb: event.target.value })} /></Alan>
            <Alan etiket={T.tedarikci}><select disabled={calisiyor !== null} data-testid="tur-pdf-manage-supplier" value={taslak.tedarikciId} onChange={(event) => setTaslak({ ...taslak, tedarikciId: event.target.value })}><option value="">{T.tedarikciYok}</option>{tedarikciler.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></Alan>
          </AlanSatiri>
          {taslak.kurallar.map((kural, sira) => <div className="tur-pdf-kural" key={sira} data-testid="tur-pdf-manage-rule">
            <Alan etiket={`${T.aranan} ${sira + 1}`}><textarea disabled={calisiyor !== null} rows={2} maxLength={2000} data-testid="tur-pdf-manage-rule-find" value={kural.ara} onChange={(event) => kuralYaz(sira, { ara: event.target.value })} /></Alan>
            <Alan etiket={`${T.yeniMetin} ${sira + 1}`} ipucu={T.silinir}><textarea disabled={calisiyor !== null} rows={2} maxLength={2000} data-testid="tur-pdf-manage-rule-replace" value={kural.yeni} onChange={(event) => kuralYaz(sira, { yeni: event.target.value })} /></Alan>
            <button type="button" className="ikincil" aria-label={`${T.kuralSil} ${sira + 1}`} disabled={calisiyor !== null || taslak.kurallar.length === 1} onClick={() => setTaslak({ ...taslak, kurallar: taslak.kurallar.filter((_, i) => i !== sira) })}>{T.kuralSil}</button>
          </div>)}
          <div className="tur-pdf-eylemler">
            <button type="button" className="ikincil" data-testid="tur-pdf-manage-add-rule" disabled={calisiyor !== null || taslak.kurallar.length >= 50} onClick={() => setTaslak({ ...taslak, kurallar: [...taslak.kurallar, { ara: '', yeni: '' }] })}>{T.kuralEkle}</button>
            <button type="button" className="birincil" data-testid="tur-pdf-manage-save" disabled={calisiyor !== null} onClick={() => void taslakKaydet()}>{T.kaydet}</button>
            <button disabled={calisiyor !== null} type="button" className="ikincil" onClick={() => setTaslak(null)}>{T.vazgec}</button>
          </div>
        </div>}
      </>}
    </div>
  </section>;
}
